import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  HeadingLevel, AlignmentType, WidthType, BorderStyle, PageOrientation,
  Header, Footer, PageNumber, ShadingType, ImageRun,
} from "docx";
import { saveAs } from "file-saver";
import { fetchPhotos, photoReport, assetBytes } from "./photos";
import coverArt from "../../../HSE_Report_Cover.png";
import logoArt from "../../../momentum_logo.png";
import { fileNameFor } from "./exportElectricalPdf";

/**
 * The report as a Word document. Ported from Csa/report/exportWord.js.
 *
 * Draws the same structure the PDF draws, from the same data, and makes no
 * decisions of its own. Word rather than PDF matters to clients who want to
 * paste findings into their own corrective-action tracker.
 *
 * No verification_method column: Electrical's checklist has no equivalent
 * field, so the register here is one column narrower than CSA's.
 */

const C = {
  ink: "1E2A28",
  slate: "5C6B68",
  tealDark: "245952",
  teal: "307268",
  gold: "EFC71D",
  sunk: "F1F4F3",
  white: "FFFFFF",
  yes: "2E7D4F",
  partial: "B8860B",
  no: "C0392B",
  na: "7C8A87",
};

const statusColour = (s) => {
  const v = String(s || "").toUpperCase();
  if (v === "YES") return C.yes;
  if (v === "PARTIAL") return C.partial;
  if (v === "NO") return C.no;
  return C.na;
};

const FONT = "Montserrat";

/* ------------------------------------------------------------------ */
/* small builders                                                      */
/* ------------------------------------------------------------------ */

const text = (t, o = {}) =>
  new TextRun({ text: String(t == null ? "" : t), font: FONT, ...o });

const para = (t, o = {}) =>
  new Paragraph({
    children: Array.isArray(t) ? t : [text(t, o.run || {})],
    spacing: { after: o.after == null ? 120 : o.after, line: 276 },
    alignment: o.align,
    heading: o.heading,
    ...(o.extra || {}),
  });

const heading = (no, title) =>
  new Paragraph({
    children: [text(`${no}. ${title}`, { bold: true, size: 28, color: C.tealDark })],
    spacing: { before: 240, after: 60 },
    heading: HeadingLevel.HEADING_1,
    border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: C.gold, space: 4 } },
  });

const noBorders = {
  top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
  left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
  insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
};

const thinBorders = {
  top: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
  bottom: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
  left: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
  right: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
  insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
  insideVertical: { style: BorderStyle.SINGLE, size: 2, color: "DCE3E1" },
};

const cell = (children, o = {}) =>
  new TableCell({
    children: Array.isArray(children) ? children : [children],
    width: o.width ? { size: o.width, type: WidthType.PERCENTAGE } : undefined,
    shading: o.fill ? { type: ShadingType.CLEAR, fill: o.fill } : undefined,
    margins: { top: 60, bottom: 60, left: 80, right: 80 },
    columnSpan: o.span,
  });

const headCell = (label, width) =>
  cell(
    new Paragraph({
      children: [text(label.toUpperCase(), { bold: true, size: 13, color: C.white })],
      spacing: { after: 0 },
    }),
    { width, fill: C.tealDark }
  );

const fmtDate = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  if (isNaN(x.getTime())) return String(d);
  return x.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const dateRange = (a, b) =>
  !a && !b ? "—" : a && b && a !== b ? `${fmtDate(a)} – ${fmtDate(b)}` : fmtDate(a || b);

const pctText = (n) => (n === null || n === undefined ? "Not assessed" : `${n}%`);

/** The line that tells two documents apart. Empty for a preview. */
function issueLine(header) {
  const i = header.issue;
  if (!i) return "";
  return [
    `Issue ${i.no}`,
    fmtDate(i.issued_at),
    `${i.assessed_count} assessed`,
    `${i.selected_count} reported`,
  ].join("  ·  ");
}

/* ------------------------------------------------------------------ */
/* sections                                                            */
/* ------------------------------------------------------------------ */

/* A4 at 96 dpi, which is the unit docx measures an image in. */
const COVER_PX = { width: 794, height: 1123 };
const HEADER_LOGO_PX = { width: 120, height: 31 };

function cover(model, art) {
  const h = model.header;
  const s = model.summary;

  const pair = (label, value) =>
    new TableRow({
      children: [
        cell(para([text(label.toUpperCase(), { bold: true, size: 14, color: C.slate })], { after: 20 }), { width: 34 }),
        cell(para([text(value, { size: 22 })], { after: 20 }), { width: 66 }),
      ],
    });

  const backdrop = art
    ? [new Paragraph({
        children: [new ImageRun({
          data: art,
          transformation: COVER_PX,
          floating: {
            horizontalPosition: { offset: 0 },
            verticalPosition: { offset: 0 },
            behindDocument: true,
          },
        })],
        spacing: { after: 0 },
      })]
    : [];

  return [
    ...backdrop,
    para([text("ELECTRICAL AUDIT", { bold: true, size: 18, color: C.teal })], { after: 200 }),
    para([text(h.organization, { bold: true, size: 52 })], { after: 60 }),
    para([text(h.site, { size: 28, color: C.slate })], { after: 400 }),

    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: noBorders,
      rows: [
        pair("Assessment dates", dateRange(h.start_date, h.end_date)),
        pair("Overall conformance",
          pctText(s.overall_pct) + (s.maturity_band ? ` — ${s.maturity_band}` : "")),
        pair("Prepared by", h.prepared_by || "—"),
        pair("Reviewed by", h.was_reviewed ? h.reviewed_by || "Reviewed" : "Not reviewed"),
        pair("Framework version", h.framework_version),
        pair("Report reference", h.report_id),
      ],
    }),
  ];
}

function narrative(section) {
  const paras = String(section.body || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return [
    heading(section.no, section.title),
    ...(paras.length
      ? paras.map((p) => para(p, { align: AlignmentType.JUSTIFIED }))
      : [para("This section has not been written.", { run: { italics: true, color: C.slate } })]),
  ];
}

function scoreAnalysis(section) {
  const d = section.data;

  const areaRows = [
    new TableRow({
      children: [
        headCell("#", 8), headCell("Area", 46), headCell("Assessed", 14),
        headCell("Score", 14), headCell("Band", 18),
      ],
      tableHeader: true,
    }),
    ...d.areas.map((a) =>
      new TableRow({
        children: [
          cell(para(String(a.no), { after: 0 })),
          cell(para(a.name, { after: 0 })),
          cell(para(`${a.assessed}/${a.total}`, { after: 0 })),
          cell(para([text(pctText(a.pct), { bold: true })], { after: 0 })),
          cell(para(a.band, { after: 0 })),
        ],
      })
    ),
  ];

  const catRows = [];
  d.areas.forEach((a) => {
    catRows.push(
      new TableRow({
        children: [
          cell(para([text(`${a.no}. ${a.name}`, { bold: true, color: C.teal })], { after: 0 }),
            { fill: C.sunk, span: 4 }),
        ],
      })
    );
    a.categories.forEach((c) => {
      catRows.push(
        new TableRow({
          children: [
            cell(para(c.name, { after: 0 }), { width: 54 }),
            cell(para(`${c.assessed}/${c.total}`, { after: 0 }), { width: 14 }),
            cell(para([text(pctText(c.pct), { bold: true })], { after: 0 }), { width: 14 }),
            cell(para(c.band, { after: 0 }), { width: 18 }),
          ],
        })
      );
    });
  });

  return [
    heading(section.no, section.title),
    para([
      text(pctText(d.overall.pct), { bold: true, size: 44, color: C.tealDark }),
      text("   " + d.overall.band, { bold: true, size: 24 }),
    ]),
    para(
      `${d.overall.assessed} of ${d.overall.total} checkpoints assessed` +
        (d.overall.not_assessed ? ` · ${d.overall.not_assessed} out of scope` : ""),
      { run: { color: C.slate, size: 18 }, after: 200 }
    ),

    para([text("By area", { bold: true, size: 22 })], { after: 80 }),
    new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: thinBorders, rows: areaRows }),

    para([text("By category", { bold: true, size: 22 })], { after: 80, extra: { spacing: { before: 300 } } }),
    new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: thinBorders, rows: catRows }),
  ];
}

function overallAssessment(section) {
  const d = section.data;
  return [
    heading(section.no, section.title),
    para([
      text(pctText(d.pct), { bold: true, size: 44, color: C.tealDark }),
      text("   " + d.band, { bold: true, size: 24 }),
    ]),
    para(d.statement, { align: AlignmentType.JUSTIFIED, after: 200 }),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: thinBorders,
      rows: [
        new TableRow({
          children: [
            headCell("Conformant", 25), headCell("Partial", 25),
            headCell("Non-conformant", 25), headCell("Not applicable", 25),
          ],
          tableHeader: true,
        }),
        new TableRow({
          children: [
            cell(para(String(d.counts.conformant), { after: 0 })),
            cell(para(String(d.counts.partial), { after: 0 })),
            cell(para(String(d.counts.non_conformant), { after: 0 })),
            cell(para(String(d.counts.not_applicable), { after: 0 })),
          ],
        }),
      ],
    }),
  ];
}

function scope(section) {
  const d = section.data;
  const out = [
    heading(section.no, section.title),
    para(d.note, { align: AlignmentType.JUSTIFIED }),
    para(
      `${d.assessed} of ${d.total} checkpoints were assessed` +
        (d.excluded ? `; ${d.excluded} were excluded.` : "."),
      { run: { color: C.slate, size: 18 }, after: 200 }
    ),
  ];

  d.exclusions.forEach((e) => {
    out.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: noBorders,
        rows: [
          new TableRow({
            children: [
              cell(
                [
                  para([text(e.name, { bold: true })], { after: 40 }),
                  para(e.reason, { run: { color: C.slate, size: 18 }, after: 0 }),
                ],
                { fill: "FFF8DC" }
              ),
            ],
          }),
        ],
      })
    );
    out.push(para("", { after: 80 }));
  });

  return out;
}

/**
 * The register, in its own landscape section — eight columns do not fit a
 * portrait page at a readable size.
 */
function findingsSection(section, header, images, assets = {}) {
  const d = section.data;
  const cols = d.columns;

  if (!d.rows.length) {
    return {
      properties: { page: { size: { orientation: PageOrientation.LANDSCAPE } } },
      headers: { default: wordHeader(header, assets.logo) },
      footers: { default: wordFooter(header) },
      children: [
        heading(section.no, section.title),
        para("No findings were selected for this report.", {
          run: { italics: true, color: C.slate },
        }),
      ],
    };
  }

  const rows = [
    new TableRow({
      children: cols.map((c) => headCell(c.label, c.width)),
      tableHeader: true,
    }),
    ...d.rows.map((r) =>
      new TableRow({
        children: [
          cell(para(String(r.sr_no), { after: 0, run: { size: 15 } }), { width: cols[0].width }),
          cell(para(r.area, { after: 0, run: { size: 15 } }), { width: cols[1].width }),
          cell(para(r.category, { after: 0, run: { size: 15 } }), { width: cols[2].width }),
          cell(
            para([text(r.status, { bold: true, size: 15, color: C.white })], { after: 0 }),
            { width: cols[3].width, fill: statusColour(r.status) }
          ),
          cell(para(r.observation, { after: 0, run: { size: 15 } }), { width: cols[4].width }),
          cell(para(r.recommendation || "—", { after: 0, run: { size: 15 } }), { width: cols[5].width }),
          cell(para(r.legal_reference || "—", { after: 0, run: { size: 15 } }), { width: cols[6].width }),
          cell(
            para(r.score === null ? "—" : `${r.score}/${r.max_pts}`, { after: 0, run: { size: 15 } }),
            { width: cols[7].width }
          ),
          cell(
            new Paragraph({ children: evidenceRuns(r.photos, images), spacing: { after: 0 } }),
            { width: cols[8].width }
          ),
        ],
      })
    ),
  ];

  return {
    properties: { page: { size: { orientation: PageOrientation.LANDSCAPE } } },
    headers: { default: wordHeader(header, assets.logo) },
    footers: { default: wordFooter(header) },
    children: [
      heading(section.no, section.title),
      para(
        `${d.selected} finding${d.selected === 1 ? "" : "s"} shown, from ${d.assessed} checkpoints assessed.`,
        { run: { color: C.slate, size: 18 }, after: 160 }
      ),
      new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: thinBorders, rows }),
    ],
  };
}

/** Section, Elaborate — one entry per finding rather than a table row. Portrait. */
function elaborateFindings(section, header, images, assets = {}) {
  const d = section.data;

  const children = [
    heading(section.no, section.title),
    para(
      `${d.selected} finding${d.selected === 1 ? "" : "s"} shown, from ${d.assessed} checkpoints assessed.`,
      { run: { color: C.slate, size: 18 }, after: 200 }
    ),
  ];

  if (!d.areas.length) {
    children.push(
      para("No findings were selected for this report.", {
        run: { italics: true, color: C.slate },
      })
    );
  }

  d.areas.forEach((a) => {
    children.push(
      para([text(a.name, { bold: true, size: 26, color: C.tealDark })], {
        after: 40, extra: { spacing: { before: 300 } },
      })
    );

    a.categories.forEach((c) => {
      children.push(
        new Paragraph({
          children: [text(c.name, { bold: true, size: 20, color: C.teal })],
          spacing: { before: 200, after: 100 },
          border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: "DCE3E1", space: 3 } },
        })
      );

      c.findings.forEach((f) => {
        children.push(
          para([
            text(f.number + "   ", { bold: true, size: 22 }),
            text(f.status_label, { bold: true, size: 18, color: statusColour(f.status) }),
          ], { after: 60 })
        );

        if (f.statement) {
          children.push(para(f.statement, { align: AlignmentType.JUSTIFIED, after: 60 }));
        }

        if (f.note) {
          children.push(
            para([
              text("Observed:  ", { bold: true, size: 17, color: C.teal }),
              text(f.note, { size: 18 }),
            ], { after: 40 })
          );
        }

        if (f.recommendation) {
          children.push(
            para([
              text("Action:  ", { bold: true, size: 17, color: C.teal }),
              text(f.recommendation, { size: 18 }),
            ], { after: 40 })
          );
        }

        const foot = [];
        if (f.legal_reference) foot.push(text(f.legal_reference, { size: 15, color: C.slate }));
        if (foot.length) children.push(para(foot, { after: 60 }));

        const shots = evidenceRuns(f.photos, images);
        const isText = (f.photos || []).length === 0 ||
          !(images && (f.photos || []).some((u) => images.get(u)));
        if ((f.photos || []).length) {
          children.push(new Paragraph({
            children: shots,
            spacing: { after: isText ? 220 : 200 },
          }));
        } else if (!foot.length) {
          children.push(para("", { after: 180 }));
        }
      });
    });
  });

  return {
    headers: { default: wordHeader(header, assets.logo) },
    footers: { default: wordFooter(header) },
    children,
  };
}

/* ------------------------------------------------------------------ */
/* chrome                                                              */
/* ------------------------------------------------------------------ */

const wordHeader = (h, logo) =>
  new Header({
    children: [
      new Paragraph({
        children: [
          ...(logo
            ? [new ImageRun({ data: logo, transformation: HEADER_LOGO_PX }),
               text("   ", { size: 14 })]
            : []),
          text(`${h.organization} — ${h.site}`, { size: 14, color: C.slate }),
        ],
        border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: "DCE3E1", space: 4 } },
      }),
    ],
  });

const wordFooter = (h) =>
  new Footer({
    children: [
      new Paragraph({
        children: [
          text(h.report_id + "    ", { size: 13, color: C.slate }),
          text(issueLine(h) + (issueLine(h) ? "    " : ""), { size: 13, color: C.slate }),
          new TextRun({ children: ["Page ", PageNumber.CURRENT, " of ", PageNumber.TOTAL_PAGES],
            size: 13, color: C.slate, font: FONT }),
        ],
        alignment: AlignmentType.RIGHT,
      }),
    ],
  });

/* ------------------------------------------------------------------ */

/** The evidence cell: the picture where it could be fetched, the count where it could not. */
function evidenceRuns(photos, images) {
  const list = photos || [];
  if (!list.length) return [new TextRun({ text: "—", size: 15, color: C.slate })];

  const runs = [];
  list.slice(0, 4).forEach((u) => {
    const data = images && images.get(u);
    if (!data) return;
    runs.push(new ImageRun({ data, transformation: { width: 78, height: 59 } }));
  });

  if (!runs.length) {
    return [new TextRun({
      text: `${list.length} photo${list.length === 1 ? "" : "s"}`,
      size: 15, color: C.slate,
    })];
  }
  return runs;
}

export function buildWordDocument(model, images, assets = {}) {
  const { header, sections } = model;
  const byKey = (k) => sections.find((s) => s.key === k);

  const render = (s) => {
    if (s.key === "score_analysis") return scoreAnalysis(s);
    if (s.key === "overall_assessment") return overallAssessment(s);
    if (s.key === "scope") return scope(s);
    return narrative(s);
  };

  const portraitBefore = sections.filter((s) => s.no < 7).flatMap(render);
  const portraitAfter = sections.filter((s) => s.no > 7).flatMap(render);

  return new Document({
    creator: header.prepared_by || "Momentum",
    title: `Electrical — ${header.organization} — ${header.site}`,
    description: "Electrical Audit",
    styles: {
      default: {
        document: { run: { font: FONT, size: 19, color: C.ink } },
      },
    },
    sections: [
      { children: cover(model, assets.cover) },
      {
        headers: { default: wordHeader(header, assets.logo) },
        footers: { default: wordFooter(header) },
        children: portraitBefore,
      },
      model.layout === "elaborate"
        ? elaborateFindings(byKey("findings"), header, images, assets)
        : findingsSection(byKey("findings"), header, images, assets),
      {
        headers: { default: wordHeader(header, assets.logo) },
        footers: { default: wordFooter(header) },
        children: portraitAfter,
      },
    ],
  });
}

export async function exportReportWord(model, opts = {}) {
  const images = await fetchPhotos(model, opts.photoBytes);

  const [cover, logo] = await Promise.all([
    assetBytes(coverArt),
    assetBytes(logoArt),
  ]);

  const doc = buildWordDocument(model, images, { cover, logo });
  const blob = await Packer.toBlob(doc);
  const name = opts.fileName || fileNameFor(model, "docx");
  saveAs(blob, name);
  return { name, bytes: blob.size, photos: photoReport(images) };
}
